/* @flow */
import * as React from 'react';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { DropTarget } from 'react-dnd';
import { NativeTypes } from 'react-dnd-html5-backend';
import { OrderedMap } from 'immutable';
import { graphql } from 'react-apollo';

import BoxBody from 'components/BoxBody';
import Button from 'components/ButtonDeprecated';
import Container from 'components/Container';
import Copy from 'components/Copy';
import Divider from 'components/Divider';
import DropZoneModal from 'components/DropZoneModal';
import FamilyActions from 'actions/FamilyActions';
import FileEntity from 'entities/File';
import gql from 'graphql-tag';
import Heading from 'components/Heading';
import Image from 'components/Image';
import LinkConstants from 'constants/LinkConstants';
import LoadingIndicator from 'components/LoadingIndicator';
import Progress from 'components/Progress';
import Row from 'components/Row';
import TitleHeader from 'components/TitleHeader';
import ToastActions from 'actions/ToastActions';
import ReduxUploadButton from 'components/ReduxUploadButton';
import { getFirstFile, uploadFile } from 'lib/fileUploadHelper';
import { uploadRequest } from 'reducers/fileUploads';

import './styles/ParentalConsentSignatureView.scss';

const CONSENT_FORM_CONTEXT = 'CONSENT_FORM_CONTEXT';

const fileUploadError = `
  There was an error uploading your photo. Please try again or email
  ${LinkConstants.SUPPORT_EMAIL}
  for help.
`;

type Props = {
  childId: number,
  formUrl: string,
  onProceed: Function,
  /* redux props */
  fileUploads: OrderedMap<*, *>,
  /* dnd props */
  connectDropTarget: Function,
  isDraggingFile?: boolean,
};

class ParentalConsentSignatureView extends React.Component<Props> {
  onUploadSuccess = (file: FileEntity) => {
    const onSuccess = () => {
      ToastActions.showSuccessToast('Photo upload successful');
      this.props.onProceed();
    };

    const onError = () => {
      ToastActions.showErrorToast(fileUploadError);
    };

    // $FlowFixMe
    FamilyActions.sendConsentFile(this.props.childId, (file.id: string)).then(onSuccess, onError);
  };

  onUploadFailed = (error: Object) => {
    if (error.field_errors) {
      ToastActions.showAPIError(error);
    } else {
      ToastActions.showErrorToast(fileUploadError);
    }
  };

  renderDropzoneModal = () => {
    const { fileUploads, isDraggingFile } = this.props;
    if (isDraggingFile) {
      return (
        <DropZoneModal
          titleDrop="Drag and drop form to upload"
          isFileUploaded={!fileUploads.isEmpty()}
          isHovering
        />
      );
    } else {
      return null;
    }
  };

  renderBody = () => {
    const { fileUploads, connectDropTarget, formUrl } = this.props;

    if (!formUrl) {
      return <LoadingIndicator align="center" />;
    }

    const file = getFirstFile(fileUploads);
    if (file && file.progressPercentage < 100) {
      return (
        <Container size="xxsmall">
          <Progress percent={file.progressPercentage} label="Uploading consent file…" />
        </Container>
      );
    } else {
      return connectDropTarget(
        <section>
          <TitleHeader title="Print and sign a form" />
          <Row size="small">
            <Image src={require('assets/images/ParentalConsentSignatureView/form.svg')} />
          </Row>
          <Row size="medium">
            <Copy>
              <Heading level="3">Step 1: Print, read, and sign the permission form</Heading>
              <Button href={formUrl} target="_blank" level="secondary" full>
                Download Form
              </Button>
              <Divider size="medium" />
              <Heading level="3">Step 2: Upload a photo of the signed permission form</Heading>
              <p>The photo should include the entire form with the signature clearly visible.</p>
            </Copy>
          </Row>
          <ReduxUploadButton
            fileContextKey={CONSENT_FORM_CONTEXT}
            onSuccess={this.onUploadSuccess}
            onFailure={this.onUploadFailed}
            full>
            Upload
          </ReduxUploadButton>
          {this.renderDropzoneModal()}
        </section>
      );
    }
  };

  render() {
    return <BoxBody className="ParentalConsentSignatureView">{this.renderBody()}</BoxBody>;
  }
}

export { ParentalConsentSignatureView };

const dndEvents = {
  drop: (props, monitor, component) => {
    const file = monitor.getItem().files[0];
    const uploadRequestWithCallbacks = (fileContextKey: string, filename: string, blob: File) => {
      props.uploadRequest(
        fileContextKey,
        filename,
        blob,
        // $FlowFixMe
        component.onUploadSuccess,
        // $FlowFixMe
        component.onUploadFailed
      );
    };
    uploadFile(CONSENT_FORM_CONTEXT, file, uploadRequestWithCallbacks);
  },
  canDrop: props => {
    return props.fileUploads.isEmpty(); // can only attach one file at a time
  },
};

const collect = (connector, monitor) => ({
  connectDropTarget: connector.dropTarget(),
  isDraggingFile: monitor.isOver(),
});

const withDropTarget = DropTarget(NativeTypes.FILE, dndEvents, collect);

const mapStateToProps = (state: Object) => {
  const { fileUploads } = state;
  const fileGroup = fileUploads.get(CONSENT_FORM_CONTEXT);
  return {
    fileUploads: fileGroup || new OrderedMap(),
  };
};

const mapDispatchToProps = (dispatch: Function) =>
  // $FlowFixMe
  bindActionCreators(
    {
      uploadRequest,
    },
    dispatch
  );

const withData = graphql(
  gql`
    query getVpcFormUrl($childId: Int!) {
      me {
        uuid
        family {
          vpcFormUrl(childId: $childId)
        }
      }
    }
  `,
  {
    options: ({ childId }) => ({
      variables: { childId },
    }),
    props: ({ data }) => ({
      formUrl: data.me ? data.me.family.vpcFormUrl : null,
    }),
  }
);

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withDropTarget(withData(ParentalConsentSignatureView)));
